/**
 * What a witness does about what they believe.
 *
 * Perception says who saw what and how sure they are. It says nothing about
 * consequences, because a claim is not a verdict. This is where a claim turns
 * into somebody's state of mind, and there are three of those:
 *
 *   CALM        they have nothing, or nothing that survived the last minute
 *   SUSPICIOUS  they think something happened and they are watching you
 *   ACCUSING    they are sure, and they are going to do something about it
 *
 * The interesting half is the way back down. Suspicion drains on its own, and
 * it drains six times faster while the person watching you can see you
 * behaving — standing there, hands empty, walking at a walk. That is the alibi,
 * and it is the whole reason being seen is not simply bad: the cheapest way out
 * of SUSPICIOUS is to be looked at for a few seconds while doing nothing wrong.
 *
 * Which means the three ways to lose it are the three things a guilty person
 * does. Run. Duck out of sight. Do it again.
 *
 * And the evidence in your hands. Somebody who watched a bottle go through a
 * window and is now looking at you holding a bottle is most of the way to sure.
 * Putting it down works — it has to, or the rule is a trap rather than a
 * pressure — but it works by letting the alibi start rather than by rewinding
 * what they saw, so there is a few seconds in which dropping it is the right
 * move and after which it is too late.
 */

import { DURATION_GAIN } from './perception.js';

export const CALM = 'calm';
export const SUSPICIOUS = 'suspicious';
export const ACCUSING = 'accusing';

/** Where the bands sit on a 0..1 level. */
const SUSPICIOUS_AT = 0.34;
/**
 * Exported because the ceiling below is derived from it and a test has to be
 * able to check that the two have not drifted apart. Nothing reads it to make
 * a decision; `stateFor` is the only thing that compares a level to a band.
 */
export const ACCUSING_AT = 0.75;

/**
 * Per second, while they are looking at the thing in your hands.
 *
 * Deliberately slow. At half a second it took under a second to cross the line,
 * which makes "put it down" a rule the player is told about rather than one
 * they can use. At this rate you have three or four seconds, which is exactly
 * long enough to notice what you are holding and do something about it.
 */
const EVIDENCE_RISE = 0.12;

/**
 * Per second, when nobody is reinforcing it — out of sight, or in sight and
 * doing something that is not behaving.
 *
 * A claim takes most of a minute to stop mattering this way, which is long
 * enough that hiding is not a strategy.
 */
const DECAY = 0.012;

/**
 * **The invariant: the alibi must out-drain a witness getting surer.**
 *
 * A witness who keeps you in view grows their claim by `DURATION_GAIN` a
 * second, and a claim growing is a claim paying out again — so while somebody
 * is watching, the level is being pushed up at `DURATION_GAIN × WEIGHT` and
 * pulled down at `ALIBI` in the same frame. Standing still in view is the
 * alibi, and the alibi only exists if the second number wins.
 *
 * It did not. Both were written as six hundredths, independently, and for every
 * claim at full weight — a theft, a breakage, somewhere you should not be — the
 * two cancelled *exactly*. The level did not drift down slowly; it did not move
 * at all, for the five or six seconds the growth took to reach its cap, and
 * then fell off a cliff. Behaving bought nothing for six seconds and everything
 * afterwards, which is not a slow alibi, it is a broken one: it meant a witness
 * who turned away inside that window lost somebody still over the line, and
 * losing you converts. A corridor incident converted six times in ten.
 *
 * So `ALIBI` is no longer a number of its own. It is `DURATION_GAIN` plus a
 * stated margin, read from the module that owns the growth, which makes the
 * relationship impossible to break by editing either one. The margin is the
 * *net* rate a behaving player drains at while still being watched, and it is
 * what the block of comment above the bands has always claimed: a clear claim
 * is under the line in under three seconds and gone in about eight.
 *
 * `noticed.test.js` and `suspicion.test.js` both fail if the margin ever
 * reaches zero, and if any weight ever exceeds one — which would put the
 * heaviest claim's growth back above the drain by another route.
 */
export const ALIBI_MARGIN = 0.04;
export const ALIBI = DURATION_GAIN + ALIBI_MARGIN;

/** Added to the target while you are visibly holding what they saw thrown. */
const EVIDENCE = 0.4;

/** Where a conversion lands you. Over the line, not miles over it. */
/**
 * Where a conversion lands you: over *your* line, not miles over it.
 *
 * Relative to the person rather than a constant, because the line moved. The
 * three ways to lose it only fire on somebody who is already suspicious, so a
 * fixed 0.80 against a raised bar would have meant a person who thinks well of
 * you cannot be converted at all rather than is harder to convert — and the
 * three rules would quietly stop existing for everybody the player is on good
 * terms with.
 */
const CONVERTED_MARGIN = 0.05;

/**
 * Seconds within which two new things a witness notices are one incident.
 *
 * Doing it again is supposed to be damning, and it is — but one bottle thrown
 * once arrives as a throw and as a breakage, correctly, because both happened.
 * Without a window on it, throwing a single bottle counted as a second offence
 * against itself and converted straight to an accusation. Two seconds apart is
 * two things; two frames apart is one thing with two names.
 */
const INCIDENT_SECONDS = 2;

/**
 * How much each kind of claim is worth worrying about.
 *
 * Perception records everything that happens, which is correct and which is
 * also why this table has to exist. Setting a round down makes a noise; that
 * noise is a real event, real witnesses hear it, and a record of it is a true
 * record. Without a weight, the Banquet Captain watched the player do the job
 * she asked for and worked herself up to writing them up for it — six rounds
 * in, the run ended on three strikes for the crime of banqueting.
 *
 * This is not a check on whether the player was caught. It is what a witness
 * makes of what they saw, which is a different question and belongs to the
 * witness. A person who watches you dress a table is not suspicious of you.
 *
 * The default is zero on purpose. A new kind of event added later is not
 * incriminating until somebody decides it is, which is the safe direction for
 * a game that is about to grow wildlife and guests.
 */
const WEIGHT = Object.freeze({
  /** Somebody else's property, in your hands or in pieces. */
  take: 1,
  break: 1,
  /** Somewhere you are not supposed to be, in something you shouldn't wear. */
  trespass: 1,
  uniform: 0.8,
  /**
   * A stranger in the back of house, which is what you are in your own clothes.
   *
   * Heavier than blacks on the marble and lighter than being in a room you have
   * no business in, and the order is the point. Somebody in banquet blacks in
   * the lobby is staff in the wrong place — irritating, and explicable. Somebody
   * in a hoodie by the linen shelves is not obviously staff at all, and the
   * people who work here do not know your face yet.
   */
  civilian: 0.9,
  /**
   * Things flying about, and things on their side. Less damning than damage,
   * more than nothing.
   *
   * An impact that broke nothing is not on this list, and that omission is the
   * difference between a working game and one that cannot be played. A cart
   * clipping a doorframe emits an impact; so does a chair set down hard. Both
   * are the sound of somebody doing the job. With a weight on them the Banquet
   * Captain watched the player dress her ballroom, took two bumps as a second
   * offence, and had them fired for banqueting — three strikes, the day
   * reloaded, and a benchmark that failed with no write-ups on the board
   * because ending the run had already cleared them.
   *
   * If something actually broke there is a break record saying so.
   */
  throw: 0.6,
  tip: 0.5,
  /** Going up the outside of something is odd rather than criminal. */
  climb: 0.25,

  /**
   * An animal somewhere people think it should not be.
   *
   * Full weight, and the only row here that is not about you. It is on this
   * table because what a person does about a herd by the dock doors is the
   * same question as what they do about a smashed bottle — are they going to
   * do something — and it is answered by the same levels, the same bands and
   * the same reactions. What it can never be is a write-up: the claim carries
   * its own subject, and your card is not it. That rule lives with whoever
   * reads accusations, not here, because this file does not know whose card
   * anything goes on.
   *
   * A herd is several animals and each one is its own claim, so a herd pays
   * out several times where one javelina pays out once. That is not a special
   * case either; it is what seeing four of something is.
   */
  wildlife: 1,

  /**
   * Somebody went past standing on a loaded cart.
   *
   * Under the line on its own, like the rest of the odd-rather-than-criminal
   * column — a corridor run does not get you written up. What it does is put a
   * quarter of a doubt into everybody who heard it, which is enough to make
   * them look up, and somebody looking up is the thing that takes your hands
   * off the handle.
   */
  ride: 0.45,

  /*
   * Being visibly not all there, and smelling of what did it.
   *
   * Small on purpose, and the size is the rule rather than a taste. A conduct
   * record tops out around nine tenths after somebody has stared at you for a
   * while; at these weights that is a quarter, which is under the line to
   * SUSPICIOUS and stays under it however long they look. So being out of it
   * never makes anybody suspicious of you by itself — and it sits under
   * everything else you do all shift, so the one thing that does go wrong
   * arrives on top of a quarter rather than on top of nothing.
   *
   * That is what "amplifies, never generates" has to mean once it is a number.
   */
  impaired: 0.3,
  scent: 0.25,

  /**
   * Being seen doing something for a laugh.
   *
   * In the odd-rather-than-criminal band with the throw, and under it: ice down
   * a collar is a thing a colleague does, and it is not a theft. Heavy enough
   * that somebody who watched you do it can get as far as doing something about
   * it, which is the whole point of a prank having a witness at all, and light
   * enough that one is not three strikes.
   */
  prank: 0.55,

  /**
   * And seeing the *result* of one on somebody else's back.
   *
   * Half, and it carries its own subject, so like a javelina it can never
   * reach your card — which is the rule that makes a kick-me note cost the
   * target nothing. What it can do is make everybody who walks past notice,
   * which is the entire content of the joke.
   */
  note: 0.5,

  /**
   * Putting something down.
   *
   * The lightest row on the table, and the only one that exists so that a thing
   * can be *reported* rather than held against you. Somebody standing next to
   * you hears a bottle meet the floor, and at five hundredths of a claim it
   * reads on the after-action record as a person who noticed and never thought
   * twice, which is exactly what it is.
   *
   * It is not zero, and the difference matters. A drop was weightless for a
   * long time — not by decision but because nothing had written a row for it —
   * and a claim weighing nothing is a claim `suspicion.js` filters out before
   * it reaches any rule at all. That is the right *outcome* and it is not a
   * rule; it is an absence of one, and the first person to give a drop a weight
   * so that it could be heard would have silently handed it every route to an
   * accusation that a smashed bottle has. `NEVER_CONVERTS` below is that rule,
   * written down.
   */
  drop: 0.05,
});

/**
 * Claims that are noticed, weighed, written down — and never convert anybody.
 *
 * Exactly one thing is on this list and it is on it for a reason the design
 * states out loud at the top of this file: putting down what somebody is
 * watching you hold is the way out of being watched holding it. "Putting it
 * down works — it has to, or the rule is a trap rather than a pressure."
 *
 * A counterplay that can finish the accusation it exists to prevent is not a
 * counterplay, it is a trap with a key-press on it. So a drop is shut out of
 * both routes a claim has to an accusation, and only those two: it can never be
 * the second thing noticed, and it can never be the payout that carries a
 * witness over the line. Everything else about it is ordinary — it is seen, it
 * is heard, it is on the record, and it sits under whatever else you did.
 */
export const NEVER_CONVERTS = Object.freeze(new Set(['drop']));

/**
 * How far under the line a never-converting claim has to leave somebody.
 *
 * Named and derived rather than written as a number, for the same reason
 * `ALIBI` is `DURATION_GAIN + ALIBI_MARGIN` rather than 0.1: the ceiling and
 * the line have to move together, and the only way to guarantee two numbers
 * move together is to write one of them once. A test fails if the margin ever
 * reaches zero, which is the moment the ceiling stops being one.
 */
export const NEVER_CONVERTS_MARGIN = 0.02;
export const NEVER_CONVERTS_CEILING = ACCUSING_AT - NEVER_CONVERTS_MARGIN;

/**
 * The heaviest a claim can be.
 *
 * The other half of the alibi invariant. What the drain actually has to beat is
 * `DURATION_GAIN × HEAVIEST`, because a claim pays out weighted — so a weight
 * above one would put the growth back over the drain from the other direction
 * without anybody touching either rate. There is a test.
 */
export const HEAVIEST = Math.max(...Object.values(WEIGHT));

/**
 * What this witness makes of one thing they believe.
 *
 * Exported because the after-action record ranks the same claims by the same
 * measure, and a second opinion about what a linen is worth against a smashed
 * bottle would be a second answer to a question this file already answers. The
 * ledger reads this table; it does not get one of its own.
 */
export const weigh = (record) => record.confidence * (WEIGHT[record.what] ?? 0);

/**
 * The act inside a claim's id, with the witness stripped off the front.
 *
 * Five people watching one armful of linen hold five records of one act. This
 * is how they are recognised as the same act, and it is exported for the same
 * reason `weigh` is: the ledger has to agree with the file about what counts as
 * one thing happening.
 */
export const actOf = (record) => record.id.slice(record.by.length + 1);

/**
 * How much it takes to get one particular person going.
 *
 * `bar` is a number added to both bands for one witness, and it is the only
 * thing in this file that knows people are different from each other. Nothing
 * here knows *why* a bar is where it is — whether somebody likes you, whether
 * they have been carrying you for a fortnight — and it must not: this module
 * answers "what does this witness make of what they saw", and who they are is
 * the roster's business and the caller's.
 *
 * Both bands move together on purpose. A person inclined to give you the
 * benefit of the doubt does not get suspicious quickly and then need extra
 * convincing; they do not get suspicious quickly, full stop, and the three ways
 * to lose it are all gated on being suspicious in the first place. Moving only
 * the top band would have left somebody bristling at you the whole time and
 * simply never saying it, which is a worse person to be in a room with than
 * either end of this.
 */
export function createSuspicion({ npcs, perception, bar = () => 0 }) {
  const levels = new Map();
  /** What each witness had last frame, to notice a second one and a vanishing. */
  const known = new Map();
  /** How much of each claim has already been paid into a level. */
  const applied = new Map();
  const wasWatching = new Set();
  /** The last place each witness actually had you. */
  const lastSeen = new Map();
  /** When each witness last noticed something new, on a summed-frame clock. */
  const noticedAt = new Map();
  let seconds = 0;
  /**
   * Acts already answered for, so one thing is answered for once.
   *
   * Keyed by the act rather than by the claim, because five people can watch
   * the same linen leave the same shelf and they each form their own record of
   * it. Keying on the record meant the Housekeeping lead and the linen room
   * both filed, which is two write-ups for one armful and, with anything else
   * already on your card, the end of the run for a single offence.
   *
   * They can all still be angry about it. Only the first one gets to file.
   */
  const answered = new Set();

  const levelOf = (id) => levels.get(id) ?? 0;

  function stateFor(level, id = null) {
    const lift = id === null ? 0 : bar(id);
    if (level >= ACCUSING_AT + lift) return ACCUSING;
    if (level >= SUSPICIOUS_AT + lift) return SUSPICIOUS;
    return CALM;
  }

  /** Where converting lands this particular person. */
  const convertedFor = (id) => ACCUSING_AT + bar(id) + CONVERTED_MARGIN;

  /**
   * One frame.
   *
   * `conduct` is what the player is visibly doing: sprinting, and what is in
   * their hands. Nothing here asks what the player *did* — only what this
   * witness believes and what they can see right now.
   */
  function update(dt, { sprinting = false, holding = null, position = null } = {}) {
    seconds += dt;
    const watchers = new Set(perception.watchers);
    const accusations = [];

    for (const npc of npcs) {
      const id = npc.data.id;
      if (!npc.present) {
        levels.delete(id);
        known.delete(id);
        noticedAt.delete(id);
        applied.delete(id);
        wasWatching.delete(id);
        continue;
      }

      const mine = perception.by(id).filter((record) => weigh(record) > 0);
      const claim = mine.reduce((best, record) => (weigh(record) > weigh(best ?? { confidence: 0, what: null }) ? record : best), null);
      const watching = watchers.has(id);
      let level = levelOf(id);

      /*
       * What they think they have, and why it is an injection rather than a
       * target.
       *
       * The obvious shape is "suspicion eases toward the strongest thing you
       * believe". It does not work, and the way it fails is instructive: a
       * claim never fades, so suspicion can never fall below it, so the alibi
       * cannot exist. Standing still and being looked at is supposed to be the
       * way out, and under a target it is a floor.
       *
       * So a claim pays out once, when it arrives and again whenever it gets
       * stronger, and after that the level is on its own and heads down. What
       * they saw is a fact; how much they still care about it is not.
       */
      const injected = applied.get(id) ?? new Map();
      for (const record of mine) {
        const already = injected.get(record.id) ?? 0;
        const worth = weigh(record);
        if (worth <= already) continue;
        const gain = worth - already;
        /*
         * A claim that never converts pays out like any other, up to a ceiling
         * it may not push anybody past. Written as a clamp on the *result*
         * rather than as a rule about the gain, because the question is not how
         * much a drop is worth — it is worth five hundredths — but whether a
         * drop can be the thing that tipped somebody over, and the answer to
         * that has to hold at every level it could arrive at.
         *
         * Anybody already above the ceiling stays where they are. A drop does
         * not talk somebody down either; it is not evidence of innocence, it is
         * just not evidence.
         */
        level = NEVER_CONVERTS.has(record.what)
          ? Math.max(level, Math.min(level + gain, NEVER_CONVERTS_CEILING))
          : level + gain;
        injected.set(record.id, worth);
      }
      applied.set(id, injected);

      // The thing in your hands is the exception, because it is not something
      // they saw once. They are looking at it now, and they keep looking.
      const evidence = claim && holding?.count && holding.kind === claim.detail && watching;
      if (evidence && level < weigh(claim) + EVIDENCE) {
        level += EVIDENCE_RISE * dt;
      }

      // Behaving means: not running, and not stood there holding it.
      const behaving = !sprinting && !evidence;
      const rate = watching && behaving ? ALIBI : DECAY;
      level = Math.max(0, Math.min(1, level - rate * dt));

      // --- the three ways to lose it ---------------------------------------
      if (stateFor(level, id) === SUSPICIOUS) {
        const seenBefore = known.get(id) ?? new Set();
        let fresh = false;
        for (const record of mine) {
          if (seenBefore.has(record.id)) continue;
          // Doing it again is damning. Tidying up after yourself is not doing
          // it again, and this is the line that says so.
          if (NEVER_CONVERTS.has(record.what)) continue;
          fresh = true;
        }
        const since = seconds - (noticedAt.get(id) ?? -Infinity);
        const second = fresh && seenBefore.size > 0 && since > INCIDENT_SECONDS;

        const vanished = wasWatching.has(id) && !watching;
        /*
         * Somebody who was suspicious and has just lost you goes and looks at
         * the last place they had you. Nothing here knows about hiding places:
         * losing sight of you is losing sight of you, whether you got under a
         * table, went round a corner, or they simply turned their head.
         */
        if (vanished) npc.goAndLook?.(lastSeen.get(id) ?? npc.position);

        if (sprinting && watching) level = convertedFor(id);
        else if (vanished) level = convertedFor(id);
        else if (second) level = convertedFor(id);
      }

      const before = known.get(id) ?? new Set();
      const after = new Set(mine.map((record) => record.id));
      for (const key of after) {
        if (!before.has(key)) { noticedAt.set(id, seconds); break; }
      }
      known.set(id, after);
      if (watching) {
        wasWatching.add(id);
        if (position) lastSeen.set(id, { x: position.x, z: position.z });
      } else {
        wasWatching.delete(id);
      }
      levels.set(id, level);

      // --- and what they do about it ---------------------------------------
      if (stateFor(level, id) === ACCUSING && claim && !answered.has(actOf(claim))) {
        answered.add(actOf(claim));
        accusations.push({ npc, record: claim, level });
      }
    }

    return accusations;
  }

  return {
    update,
    levelOf,
    stateOf: (id) => stateFor(levelOf(id), id),

    /** Where each witness last had you. */
    lastSeenBy: (id) => lastSeen.get(id) ?? null,

    /** Everybody who is past CALM, worst first. Drives the HUD. */
    get watching() {
      return [...levels.entries()]
        .filter(([, level]) => level >= SUSPICIOUS_AT)
        .sort((a, b) => b[1] - a[1])
        .map(([id, level]) => ({ id, level, state: stateFor(level, id) }));
    },

    /** The highest level anybody holds. */
    get worst() {
      let most = 0;
      for (const level of levels.values()) most = Math.max(most, level);
      return most;
    },

    /**
     * Talking somebody down.
     *
     * Takes a fixed bite out of what they think, and it is refused outright by
     * anybody already ACCUSING — past that point they are not listening to you,
     * they are looking for a manager, and a charm offensive aimed at somebody
     * who has already decided is the most obviously guilty thing a person can
     * do. Which is why it costs: a refused one hardens them.
     */
    charm(id, { bite = 0.3, backfire = 0.12 } = {}) {
      const level = levelOf(id);
      if (stateFor(level, id) === ACCUSING) {
        levels.set(id, Math.min(1, level + backfire));
        return { ok: false, reason: 'accusing' };
      }
      levels.set(id, Math.max(0, level - bite));
      return { ok: true, was: level, now: levelOf(id) };
    },

    /**
     * An accusation nobody acted on, handed back.
     *
     * Emitting one marks its act answered, because one act is answered once.
     * But an accusation that could not be acted on — off the clock, where
     * nothing is filed — has not answered anything, and keeping the mark meant
     * a witness who saw your civvies on the walk in could never write you up
     * for them on shift. Released, the act is open again: whoever is sure of it
     * next, on the clock, can still do something about it.
     *
     * Nothing else about what they believe changes: the record stays, the
     * level stays, and for as long as they stay sure off the clock they are
     * released again each frame — sure, and unable to do anything about it.
     * The next time they get sure on the clock, the way anybody does, it
     * lands.
     */
    release(accusation) {
      return answered.delete(actOf(accusation.record));
    },

    /** After somebody has acted: they have had their say and calm down. */
    settle(id) {
      levels.set(id, SUSPICIOUS_AT * 0.5);
      // And they are square with everything they currently believe, or the
      // same claim pays out again the moment they have finished speaking.
      const injected = applied.get(id) ?? new Map();
      for (const record of perception.by(id)) injected.set(record.id, weigh(record));
      applied.set(id, injected);
    },

    clear() {
      levels.clear();
      known.clear();
      lastSeen.clear();
      noticedAt.clear();
      applied.clear();
      wasWatching.clear();
      answered.clear();
    },
  };
}
